import { inject, observer } from 'mobx-react';
import ButtonToolbar from 'react-bootstrap/ButtonToolbar';
import {
  useEffect,
  useState,
} from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { toJS } from 'mobx';

import { dateTimeStringToUnix, unixToDateTimeString } from 'modules/core/utils/date';
import { DataTablePage } from '../../core/containers';
import { Button } from '../../core/components/buttons';
import {
  promoActions,
  PromoStore,
} from '../store/promoStore';
import { QuickSearch } from '../../core/components/search';
import ClientPromoEdit from './ClientPromoEdit';
import { ClientPromoInterface } from '../api/promoApi';

type ClientPromoRowType = { row: ClientPromoInterface }

interface ClientPromoListProps {
  promoStore?: PromoStore,
  clientId?: string,
  shortMode?: boolean,
  noDocumentTitle?: boolean,
}

const columns = [
  {
    title: 'Акция',
    name: 'promo.name',
    width: 155,
    getCellValue: (row:ClientPromoInterface) => (row?.promo?.name)
    && (
      <span>{ row?.promo?.name }</span>
    ),
  },
  {
    title: 'Начало акции',
    name: 'promo.startDate',
    width: 155,
    getCellValue: (row:ClientPromoInterface) => (row?.promo?.startDate)
    && (
      <span>{ row?.promo?.startDate }</span>
    ),
  },
  {
    title: 'Окончание акции',
    name: 'promo.endDate',
    width: 155,
    getCellValue: (row:ClientPromoInterface) => (row?.promo?.endDate)
    && (
      <span>{ row?.promo?.endDate }</span>
    ),
  },
  {
    title: 'Max использований',
    name: 'promo.maxNumberOfUses',
    width: 155,
    getCellValue: (row:ClientPromoInterface) => (row?.promo?.maxNumberOfUses)
    && (
      <span>{ row?.promo?.maxNumberOfUses }</span>
    ),
  },
  {
    title: 'Скидка,руб',
    name: 'promo.discount',
    width: 155,
    getCellValue: (row:ClientPromoInterface) => (row?.promo?.discount)
    && (
      <span>{ row?.promo?.discount }</span>
    ),
  },
  {
    title: 'Скидка,%',
    name: 'promo.discountPercent',
    width: 155,
    getCellValue: (row:ClientPromoInterface) => (row?.promo?.discountPercent)
    && (
      <span>{ row?.promo?.discountPercent }</span>
    ),
  },
  { title: 'comment', name: 'comment', width: 220 },
  { title: 'Использований', name: 'usedCount', width: 170 },
  {
    title: 'Активность',
    name: 'deletedDate',
    width: 145,
    getCellValue: (row:ClientPromoInterface) => (row.deletedDate)
    && (
      <div className='marked-content'>
        <span>неактивный</span>
      </div>
    ),
  },
  { title: 'Источник', name: 'sourceProjectName', width: 180 },
  { title: 'Кто начислил', name: 'createdByUserName', width: 200 },
  {
    title: 'Дата начисления',
    name: 'createdDate',
    width: 180,
    getCellValue: (row:ClientPromoInterface) => {
      return row?.createdDate ? unixToDateTimeString(row?.createdDate, 'DD.MM.YYYY HH:mm') : '';
    },
    sortValueConverter: (value) => (value ? dateTimeStringToUnix(value) : ''),
  },
];

const columnsShort = [
  {
    title: 'Акция',
    name: 'promo.name',
    width: 155,
    getCellValue: (row:ClientPromoInterface) => (row?.promo?.name)
    && (
      <span>{ row?.promo?.name }</span>
    ),
  },
  {
    title: 'Начало акции',
    name: 'promo.startDate',
    width: 155,
    getCellValue: (row:ClientPromoInterface) => (row?.promo?.startDate)
    && (
      <span>{ row?.promo?.startDate }</span>
    ),
  },
  {
    title: 'Окончание акции',
    name: 'promo.endDate',
    width: 155,
    getCellValue: (row:ClientPromoInterface) => (row?.promo?.endDate)
    && (
      <span>{ row?.promo?.endDate }</span>
    ),
  },
  {
    title: 'Скидка,руб',
    name: 'promo.discount',
    width: 155,
    getCellValue: (row:ClientPromoInterface) => (row?.promo?.discount)
    && (
      <span>{ row?.promo?.discount }</span>
    ),
  },
  {
    title: 'Скидка,%',
    name: 'promo.discountPercent',
    width: 155,
    getCellValue: (row:ClientPromoInterface) => (row?.promo?.discountPercent)
    && (
      <span>{ row?.promo?.discountPercent }</span>
    ),
  },
  { title: 'Использований', name: 'usedCount', width: 170 },
];

const ClientPromoList = (props: ClientPromoListProps) => {
  const {
    promoStore,
    clientId,
    shortMode,
    noDocumentTitle,
  } = props;

  const { getClientPromosByClientId } = promoStore;
  const clientPromos = toJS(promoStore.clientPromos);
  const currentClientPromo = toJS(promoStore.currentClientPromo);

  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [showClientPromoEdit, setShowClientPromoEdit] = useState<boolean>(false);

  const loadData = async (anyway = false) => {
    setLoading(true);
    try {
      if (anyway || (clientId !== getClientPromosByClientId)) {
        await promoActions.getClientPromoList(clientId);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChangeQuickSearch = (event) => {
    const { value } = event.target || {};

    setSearchValue((value?.length > 0) ? value : '');
  };

  const handleSubmitQuickSearch = (value) => {
    handleChangeQuickSearch({ target: { value } });
  };

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    if (currentClientPromo !== null) {
      setShowClientPromoEdit(true);
    }
  }, [currentClientPromo]);

  useEffect(() => {
    promoActions.clearError();
  }, []);

  const handleRowClick = async (tableRow: ClientPromoRowType) => {
    await promoActions.setCurrentClientPromo(tableRow.row);
  };

  const clientPromosFiltered = clientPromos?.filter((item) => !item.deletedDate);

  return (
    <Col md={ 12 }>
      <Row>
        <Col>
          <DataTablePage
            loading={ loading }
            header='Промоакции клиента'
            columns={ shortMode ? columnsShort : columns }
            data={ clientPromosFiltered }
            dataLength={ clientPromosFiltered?.length }
            error={ promoStore.error }
            noDocumentTitle = { noDocumentTitle }
          >
            { ({ components }) => (
              <>
                <Row>
                  <Col md={ 7 }>
                    <components.PageHeader />
                  </Col>

                  <Col md={ 5 } xs={ 12 }>
                    <QuickSearch
                      name='searchText'
                      value={ searchValue }
                      placeholder='Быстрый поиск'
                      disabled={ loading }
                      onClear={ handleSubmitQuickSearch }
                      onChange={ handleChangeQuickSearch }
                      onSubmit={ handleSubmitQuickSearch }
                    />
                  </Col>
                </Row>

                { !shortMode
                && (
                  <Row>
                    <Col md={ 12 }>
                      <ButtonToolbar className='page-buttons float-end align-items-start'>
                        <Button
                          variant='primary'
                          text='Добавить'
                          icon='plus'
                          // disabled={ loading || readOnly || !permissions?.permissionInsert }
                          onClick={ () => { setShowClientPromoEdit(true); } }
                        />
                      </ButtonToolbar>
                    </Col>
                  </Row>
                ) }

                <Row>
                  <Col lg={ 12 }>
                    <components.DataTable
                      //  columnsResizingMode='nextColumn'
                      searchValue={ searchValue || '' }
                      errorRetryHandler={ loadData }
                      onRowClick={ handleRowClick }
                      hideToolbar
                      localSorting
                      localPaging
                    />
                  </Col>
                </Row>
                { showClientPromoEdit && (
                  <ClientPromoEdit
                    clientId={ clientId }
                    onClose={ () => {
                      setShowClientPromoEdit(false);
                      promoActions.clearCurrentPromo();
                    } }
                    onSubmit={ () => {
                      setShowClientPromoEdit(false);
                      promoActions.clearCurrentPromo();
                      // loadData(true); // По идее, это не надо, т.к. в Store всё обновляю.
                      // onClose и onSubmit можно объединить
                    } }
                  />
                ) }
              </>
            ) }
          </DataTablePage>
          
        </Col>
      </Row>
    </Col>
  );
};

export default inject('promoStore')(observer(ClientPromoList));
